import { queryOptions, useQuery } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";

import {
  getCalendarSettingsFn,
  getCalendarSummaryFn,
  getSchedulerCalendarEventsFn,
} from "~/lib/functions/calendar";

export const calendarKeys = {
  all: ["calendar"] as const,
  summary: () => [...calendarKeys.all, "summary"] as const,
  settings: () => [...calendarKeys.all, "settings"] as const,
  schedulerEvents: (start: string) =>
    [...calendarKeys.all, "scheduler-events", { start }] as const,
};

/** Whether she has Settings › Calendar and whether to ask her to reconnect. */
export const calendarSummaryQueryOptions = () =>
  queryOptions({
    queryKey: calendarKeys.summary(),
    queryFn: () => getCalendarSummaryFn(),
    staleTime: 5 * 60 * 1000,
  });

/**
 * Lists her calendars through Composio, which the free plan counts, so a
 * window refocus does not read them again.
 */
export const calendarSettingsQueryOptions = () =>
  queryOptions({
    queryKey: calendarKeys.settings(),
    queryFn: () => getCalendarSettingsFn(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

/**
 * Her own calendar events on one day of the session scheduler, in her
 * browser's day. Asks only when she has the calendar page; each day is read
 * through Composio at most once in five minutes.
 */
export function useSchedulerCalendarEvents(day: Date | undefined) {
  const summary = useQuery(calendarSummaryQueryOptions());
  const start = day ? startOfDay(day).toISOString() : "";
  return useQuery({
    queryKey: calendarKeys.schedulerEvents(start),
    queryFn: () =>
      getSchedulerCalendarEventsFn({
        data: { start, end: endOfDay(new Date(start)).toISOString() },
      }),
    enabled: Boolean(day) && summary.data?.available === true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
